import { chain } from 'lodash';
import { useMemo } from 'react';
import {
  Columns,
  FilterOptionServerSide,
  FilterOptionsServerSide,
} from '~components/displays/DisplayTable/constants';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { DataWithCustomer } from '~pages/[organization]/customers/[handle]/displays/Index';
import { useFeatureFlag } from '~utils/features';
import { isDefined } from '~utils/types';

export function useFilterOptionsServerSide(
  filters: Array<{ id: string; value: FilterOptionServerSide[] }>,
  data: DataWithCustomer,
): FilterOptionsServerSide {
  const { isEnabled: isSchedulesEnabled } = useFeatureFlag('schedules');
  const filterMap = new Map(filters.map(({ id, value }) => [id, value]));
  const enabledGroupFilters: FilterOptionServerSide[] = filterMap.get('groups') ?? [];
  const enabledSiteFilters: FilterOptionServerSide[] = filterMap.get('site') ?? [];
  const enabledFirmwareFilters: FilterOptionServerSide[] = filterMap.get('firmware') ?? [];
  const enabledPlaylistFilters: FilterOptionServerSide[] = filterMap.get('playlist') ?? [];
  const enabledPowerScheduleFilters: FilterOptionServerSide[] =
    filterMap.get('powerSchedule') ?? [];
  const enabledWarningsFilters: FilterOptionServerSide[] = filterMap.get('warnings') ?? [];
  const enabledStatusFilters: FilterOptionServerSide[] = filterMap.get('status') ?? [];
  const enabledConfigurationFilters: FilterOptionServerSide[] =
    filterMap.get('configuration') ?? [];

  const groupFilters = useMemo<FilterOptionServerSide[]>(() => {
    return chain(data?.customer?.groups)
      .sortBy((g) => g.name.toLowerCase())
      .map(
        (group): FilterOptionServerSide => ({
          value: group.id,
          label: group.name,
          column: Columns.Groups,
          id: group.id,
        }),
      )
      .value();
  }, [data]);

  const siteFilters = useMemo<FilterOptionServerSide[]>(() => {
    return chain(data?.customer?.sites)
      .sortBy((l) => l.name.toLowerCase())
      .map(
        (site): FilterOptionServerSide => ({
          value: site.id,
          label: site.name,
          column: Columns.Site,
          id: site.id,
        }),
      )
      .value();
  }, [data]);

  const firmwareFilters = useMemo<FilterOptionServerSide[]>(() => {
    return chain(data?.customer?.displayFirmwareVersions)
      .map((row) => row)
      .filter(isDefined)
      .uniq()
      .sortBy((f) => f.toLowerCase())
      .map(
        (firmware): FilterOptionServerSide => ({
          value: firmware,
          label: firmware,
          column: Columns.Firmware,
          id: firmware,
        }),
      )
      .value();
  }, [data]);

  const playlistFilters = useMemo<FilterOptionServerSide[]>(() => {
    return chain(data?.customer?.playlists)
      .map((row) => ({
        title: row.title,
        id: row.id,
      }))
      .filter(isDefined)
      .uniq()
      .sortBy((title) => title)
      .map(
        ({ title, id }): FilterOptionServerSide => ({
          value: id,
          label: title,
          column: Columns.Playlist,
          id: id,
        }),
      )
      .value();
  }, [data]);

  const configurationFilters = useMemo<FilterOptionServerSide[]>(() => {
    return chain(data?.customer?.assignedConfigurations)
      .map((row) => ({
        title: row,
        id: row,
        state: row.state,
      }))
      .filter(isDefined)
      .filter((config) => config.state === ConfigurationStatus.Published)
      .uniq()
      .sortBy((title) => title)
      .map(
        ({ title, id }): FilterOptionServerSide => ({
          value: title.id,
          label: title.name,
          column: Columns.Configuration,
          id: id.id,
        }),
      )
      .value();
  }, [data]);

  const powerScheduleFilters = useMemo<FilterOptionServerSide[]>(() => {
    const rows: Array<{ id: string; title: string }> = isSchedulesEnabled
      ? data?.customer?.schedules ?? []
      : data?.customer?.powerSchedules ?? [];

    return chain(rows)
      .map((row) => ({
        title: row.title,
        id: row.id,
      }))
      .filter(isDefined)
      .uniq()
      .sortBy((title) => title)
      .map(
        ({ title, id }): FilterOptionServerSide => ({
          value: id,
          label: title,
          column: Columns.PowerSchedule,
          id: id,
        }),
      )
      .value();
  }, [data, isSchedulesEnabled]);

  const warningsFilters = useMemo<FilterOptionServerSide[]>(() => {
    return chain(data?.customer?.warningList)
      .map((row) => ({
        title: row.label,
        id: row.key,
      }))
      .filter(isDefined)
      .uniq()
      .sortBy((title) => title)
      .map(
        ({ title, id }): FilterOptionServerSide => ({
          value: id,
          label: title,
          column: Columns.Warnings,
          id: id,
        }),
      )
      .value();
  }, [data?.customer?.warningList]);

  const statusFilters = useMemo<FilterOptionServerSide[]>(() => {
    return [
      {
        value: 'Connected',
        label: 'Connected',
        column: Columns.Status,
        id: 'CONNECTED',
      },
      {
        value: 'Disconnected',
        label: 'Disconnected',
        column: Columns.Status,
        id: 'DISCONNECTED',
      },
      {
        value: 'Standby',
        label: 'Standby',
        column: Columns.Status,
        id: 'STANDBY',
      },
      {
        value: 'On',
        label: 'On',
        column: Columns.Status,
        id: 'ON',
      },
    ];
  }, []);

  return {
    [Columns.Firmware]: {
      all: firmwareFilters,
      enabled: enabledFirmwareFilters.map((f) => firmwareFilters.find((fw) => fw.id === f.id) ?? f),
    },
    [Columns.Groups]: {
      all: groupFilters,
      enabled: enabledGroupFilters.map((f) => groupFilters.find((g) => g.id === f.id) ?? f),
    },
    [Columns.Site]: {
      all: siteFilters,
      enabled: enabledSiteFilters.map((f) => siteFilters.find((s) => s.id === f.id) ?? f),
    },
    [Columns.Playlist]: {
      all: playlistFilters,
      enabled: enabledPlaylistFilters.map((f) => playlistFilters.find((p) => p.id === f.id) ?? f),
    },
    [Columns.PowerSchedule]: {
      all: powerScheduleFilters,
      enabled: enabledPowerScheduleFilters.map(
        (f) => powerScheduleFilters.find((p) => p.id === f.id) ?? f,
      ),
    },
    [Columns.Warnings]: {
      all: warningsFilters,
      enabled: enabledWarningsFilters.map((f) => warningsFilters.find((w) => w.id === f.id) ?? f),
    },
    [Columns.Status]: {
      all: statusFilters,
      enabled: enabledStatusFilters.map((f) => statusFilters.find((s) => s.id === f.id) ?? f),
    },
    [Columns.ClaimCode]: {
      all: [],
      enabled: [],
    },
    [Columns.Configuration]: {
      all: configurationFilters,
      enabled: enabledConfigurationFilters.map(
        (f) => configurationFilters.find((c) => c.id === f.id) ?? f,
      ),
    },
  };
}
